import React from 'react';
import { AppColors } from '../../theme/colors';
import type { StudentPromptsData, StudentPromptDisplay } from '../../types/dashboard';

interface StudentPromptsSectionProps {
  data: StudentPromptsData | null;
  loading: boolean;
}

// Helper function for relative time
const formatRelativeTime = (isoString: string): string => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Individual prompt card
const PromptCard: React.FC<{ prompt: StudentPromptDisplay }> = ({ prompt }) => {
  const isCustomLesson = prompt.type === 'custom_lesson';

  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        borderRadius: 'clamp(10px, 2.5vw, 12px)',
        padding: 'clamp(12px, 3vw, 14px) clamp(14px, 3.5vw, 16px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        transition: 'all 0.2s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Icon */}
        <div
          style={{
            width: 'clamp(32px, 8vw, 36px)',
            height: 'clamp(32px, 8vw, 36px)',
            borderRadius: 'clamp(8px, 2vw, 10px)',
            background: isCustomLesson
              ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)'
              : 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: 'clamp(14px, 3.5vw, 16px)',
          }}
        >
          {isCustomLesson ? '📚' : '🎯'}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '4px',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: 'clamp(13px, 3vw, 14px)',
                fontWeight: 600,
                color: AppColors.textPrimary,
              }}
            >
              {isCustomLesson ? prompt.title : 'Pronunciation Practice'}
            </span>
            <span
              style={{
                fontSize: 'clamp(10px, 2.2vw, 11px)',
                padding: '2px 6px',
                borderRadius: '4px',
                background: isCustomLesson ? 'rgba(139, 92, 246, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                color: isCustomLesson ? '#a78bfa' : '#60a5fa',
                fontWeight: 500,
              }}
            >
              {isCustomLesson ? 'Lesson' : 'Words'}
            </span>
          </div>

          <p
            style={{
              fontSize: 'clamp(12px, 2.5vw, 13px)',
              color: AppColors.textSecondary,
              margin: '0 0 6px 0',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {isCustomLesson ? prompt.description : prompt.words}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px' }}>👤</span>
            <span
              style={{
                fontSize: 'clamp(11px, 2.3vw, 12px)',
                color: AppColors.textSecondary,
              }}
            >
              {prompt.studentName}
            </span>
            <span
              style={{
                fontSize: 'clamp(11px, 2.3vw, 12px)',
                color: 'rgba(255,255,255,0.3)',
              }}
            >
              {formatRelativeTime(prompt.createdAt)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Loading skeleton
const LoadingSkeleton: React.FC = () => (
  <div
    style={{
      background: 'rgba(255, 255, 255, 0.03)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: 'clamp(16px, 4vw, 20px)',
      padding: 'clamp(20px, 5vw, 28px)',
      textAlign: 'center',
      color: AppColors.textSecondary,
      fontSize: 'clamp(14px, 3vw, 15px)',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      marginBottom: 'clamp(20px, 5vw, 28px)',
    }}
  >
    <div
      style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        border: '3px solid rgba(139, 92, 246, 0.2)',
        borderTopColor: AppColors.accentPurple,
        animation: 'spin 1s linear infinite',
        margin: '0 auto 12px',
      }}
    />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    Loading student topics...
  </div>
);

// Empty state
const EmptyState: React.FC = () => (
  <div
    style={{
      textAlign: 'center',
      padding: 'clamp(20px, 5vw, 32px) clamp(16px, 4vw, 24px)',
      background: 'rgba(255, 255, 255, 0.02)',
      borderRadius: 'clamp(12px, 3vw, 16px)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
    }}
  >
    <div style={{ fontSize: '32px', marginBottom: '12px' }}>💭</div>
    <p
      style={{
        fontSize: 'clamp(13px, 3vw, 14px)',
        color: AppColors.textSecondary,
        margin: 0,
      }}
    >
      No student practice topics this period
    </p>
    <p
      style={{
        fontSize: 'clamp(12px, 2.5vw, 13px)',
        color: 'rgba(255,255,255,0.4)',
        margin: '4px 0 0 0',
      }}
    >
      Topics will appear when students create custom lessons or practice pronunciation
    </p>
  </div>
);

export const StudentPromptsSection: React.FC<StudentPromptsSectionProps> = ({
  data,
  loading,
}) => {
  if (loading) {
    return <LoadingSkeleton />;
  }

  const { prompts = [], summary } = data || {
    prompts: [],
    summary: { customLessons: 0, pronunciationSessions: 0, uniqueStudents: 0 },
  };

  const totalPrompts = prompts.length;

  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: 'clamp(16px, 4vw, 20px)',
        padding: 'clamp(20px, 5vw, 28px)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        marginBottom: 'clamp(20px, 5vw, 28px)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 'clamp(16px, 4vw, 20px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>💡</span>
          <div>
            <h3
              style={{
                fontSize: 'clamp(15px, 3.5vw, 17px)',
                fontWeight: 600,
                margin: 0,
                color: AppColors.textPrimary,
              }}
            >
              Student Practice Topics
            </h3>
            {totalPrompts > 0 && (
              <p
                style={{
                  fontSize: 'clamp(12px, 2.5vw, 13px)',
                  color: AppColors.textSecondary,
                  margin: '2px 0 0 0',
                }}
              >
                What your students are choosing to practice
              </p>
            )}
          </div>
        </div>

        {/* Summary stats */}
        {totalPrompts > 0 && (
          <div
            style={{
              display: 'flex',
              gap: 'clamp(8px, 2vw, 12px)',
              fontSize: 'clamp(11px, 2.5vw, 12px)',
              color: AppColors.textSecondary,
            }}
          >
            {summary.customLessons > 0 && (
              <span
                style={{
                  padding: '4px 8px',
                  background: 'rgba(139, 92, 246, 0.1)',
                  borderRadius: '6px',
                }}
              >
                📚 {summary.customLessons}
              </span>
            )}
            {summary.pronunciationSessions > 0 && (
              <span
                style={{
                  padding: '4px 8px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '6px',
                }}
              >
                🎯 {summary.pronunciationSessions}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      {totalPrompts === 0 ? (
        <EmptyState />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {prompts.slice(0, 10).map((prompt) => (
            <PromptCard key={prompt.id} prompt={prompt} />
          ))}
          {totalPrompts > 10 && (
            <p
              style={{
                fontSize: 'clamp(12px, 2.5vw, 13px)',
                color: AppColors.textSecondary,
                textAlign: 'center',
                margin: '8px 0 0 0',
              }}
            >
              + {totalPrompts - 10} more topics
            </p>
          )}
        </div>
      )}
    </div>
  );
};
